/**
 * Text for a clicked feature's popup (Google Maps "info window"), shared by
 * the info window header (built here) and the body (LayerPopup.jsx).
 *
 * Every value here comes from outside (feature properties from external APIs,
 * labels from the engine), so it is only ever used as TEXT: JSX text children
 * in LayerPopup, `textContent` for the header elements below. Never innerHTML.
 */

/**
 * Plain-text form of an untrusted value. Objects are shown as JSON text
 * rather than "[object Object]"; null/undefined become an empty string.
 */
export function displayValue(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') {
        try {
            return JSON.stringify(value);
        } catch {
            return '';
        }
    }
    return String(value);
}

/**
 * What a popup field shows when the feature has no value for it. The same
 * words as the legend's no_data entry (engine NO_DATA_STYLE, a constant in
 * engine/tools/map_payload.py), so a gray tract and its popup agree.
 */
export const POPUP_NO_DATA = 'No data';

/**
 * Plain-text form of one popup field's value, for the popup body.
 *
 * A missing value (null, undefined, or a string of only whitespace) reads
 * "No data", the same words the legend uses for that feature, instead of a
 * label with nothing under it. Other strings are shown as sent, untrimmed.
 *
 * Numbers are shown with at most 3 decimals (external services send values
 * like 68.19616570392193), except numbers between -1 and 1, which keep 3
 * significant digits instead so a small reading (0.00012) does not round to
 * a misleading "0".
 * Grouping is off on purpose: numeric IDs and years (a ZIP code, 2024) would
 * otherwise print as "80,204" and "2,024".
 *
 * Kept separate from displayValue() because the header uses that one, and a
 * missing title must stay '' so the header falls back to the layer name.
 */
export function popupFieldValue(value) {
    if (
        value === null ||
        value === undefined ||
        (typeof value === 'string' && value.trim() === '')
    ) {
        return POPUP_NO_DATA;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        // toLocaleString prints -0 as "-0" (String() printed "0"), and
        // Python can send -0.0, so it is written as plain 0.
        if (value === 0) {
            return '0';
        }
        const digits =
            Math.abs(value) < 1
                ? { maximumSignificantDigits: 3 }
                : { maximumFractionDigits: 3 };
        return value.toLocaleString(undefined, {
            ...digits,
            useGrouping: false,
        });
    }
    return displayValue(value);
}

/**
 * The two header lines: the layer (dataset) name, and the clicked feature's
 * title from the layer's `popup.title_field` ('' when there is no popup spec
 * or the feature has no value for it).
 */
export function popupHeaderText(layer, feature) {
    const titleField = layer.popup?.title_field;
    const title = titleField
        ? displayValue(feature?.properties?.[titleField]).trim()
        : '';
    return { layerName: displayValue(layer.label), title };
}

/** Accessible name for the info window: "Feature title, Layer name". */
export function popupAriaLabel(layer, feature) {
    const { layerName, title } = popupHeaderText(layer, feature);
    return title ? `${title}, ${layerName}` : layerName;
}

/**
 * The info window header (Google Maps `headerContent`), as a DOM element.
 *
 * Two lines: a small, muted "eyebrow" with the layer name (so a multi-layer
 * map says which dataset this is, without competing with the item itself),
 * then the feature's title in bold. With no title, the layer name alone is
 * the bold line, so the header is never an empty strip next to the close
 * button. Built with createElement + textContent so text can never render as
 * markup; classes are Bootstrap utilities.
 */
export function buildPopupHeader(layer, feature) {
    const { layerName, title } = popupHeaderText(layer, feature);
    const header = document.createElement('div');
    // pb-2: breathing room between the header and the scrolling fields.
    header.className = 'bili-map__popup-header pb-2';

    const line = (text, className) => {
        const span = document.createElement('span');
        span.className = className;
        span.textContent = text;
        header.append(span);
    };

    if (title) {
        line(layerName, 'd-block small text-uppercase text-muted');
        line(title, 'd-block fw-semibold');
    } else {
        line(layerName, 'd-block fw-semibold');
    }
    return header;
}
